import { useState, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { showError } from "@/lib/toastHelpers";
import { Button } from "@/components/ui/button";
import { Mail, MailOpen, Send, ChevronLeft } from "lucide-react";

function MessageBubble({ msg }: { msg: any }) {
  const isOutbound = msg.direction === "outbound";
  return (
    <div className={`flex ${isOutbound ? "justify-end" : "justify-start"} mb-4`}>
      <div className={`max-w-[75%] rounded-lg px-4 py-3 ${isOutbound ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-800"}`}>
        <p className="text-xs opacity-70 mb-1">{isOutbound ? "You" : msg.from} · {new Date(msg.sentAt).toLocaleString()}</p>
        <p className="text-sm whitespace-pre-wrap">{msg.body}</p>
      </div>
    </div>
  );
}

export default function Inbox() {
  const qc = useQueryClient();
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [replyBody, setReplyBody] = useState("");
  const [isSending, setIsSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  const { data: threadsData, isLoading } = useQuery({
    queryKey: ["/api/admin/inbox"],
    queryFn: () => apiRequest("GET", "/api/admin/inbox"),
  });

  const threads: any[] = (threadsData as any)?.threads ?? [];

  const { data: selected, isLoading: isLoadingThread } = useQuery({
    queryKey: ["/api/admin/inbox", selectedThreadId],
    queryFn: () => apiRequest("GET", `/api/admin/inbox/${selectedThreadId}`) as unknown as Promise<any>,
    enabled: !!selectedThreadId,
  });

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [selected]);

  const openThread = (thread: any) => {
    setSelectedThreadId(thread._id);
  };

  const sendReply = async () => {
    if (!replyBody.trim() || !selectedThreadId) return;
    setIsSending(true);
    try {
      await apiRequest("POST", `/api/admin/inbox/${selectedThreadId}/reply`, { body: replyBody });
      setReplyBody("");
      qc.invalidateQueries({ queryKey: ["/api/admin/inbox", selectedThreadId] });
      qc.invalidateQueries({ queryKey: ["/api/admin/inbox"] });
    } catch { showError("Failed to send reply"); }
    finally { setIsSending(false); }
  };

  const unread = threads.filter(t => !t.read).length;

  return (
    <div className="flex h-[calc(100vh-64px)] p-6 gap-4 overflow-hidden">

      {/* Thread list */}
      <div className="w-80 bg-white rounded-lg border flex flex-col shrink-0">
        <div className="p-4 border-b">
          <h1 className="text-lg font-bold text-gray-900">Inbox</h1>
          {unread > 0 && <p className="text-xs text-blue-600 mt-0.5">{unread} unread</p>}
        </div>
        {isLoading ? (
          <p className="text-center text-gray-400 py-12 text-sm">Loading...</p>
        ) : threads.length === 0 ? (
          <p className="text-center text-gray-400 py-12 text-sm">No messages yet</p>
        ) : (
          <div className="flex-1 overflow-y-auto">
            {threads.map(thread => (
              <div key={thread._id} onClick={() => openThread(thread)}
                className={`p-4 border-b cursor-pointer hover:bg-gray-50 transition-colors ${selectedThreadId === thread._id ? "bg-blue-50 border-l-4 border-l-blue-500" : ""}`}>
                <div className="flex items-start gap-2">
                  {thread.read
                    ? <MailOpen size={15} className="text-gray-400 mt-0.5 shrink-0" />
                    : <Mail size={15} className="text-blue-600 mt-0.5 shrink-0" />}
                  <div className="min-w-0">
                    <p className={`text-sm truncate ${!thread.read ? "font-semibold" : "text-gray-700"}`}>{thread.userEmail}</p>
                    <p className={`text-xs truncate ${!thread.read ? "font-medium text-gray-800" : "text-gray-500"}`}>{thread.subject}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{new Date(thread.lastMessageAt).toLocaleString()}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Thread detail */}
      <div className="flex-1 bg-white rounded-lg border flex flex-col overflow-hidden">
        {!selectedThreadId ? (
          <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">Select a conversation</div>
        ) : isLoadingThread ? (
          <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">Loading...</div>
        ) : (
          <>
            <div className="p-4 border-b flex items-center gap-3">
              <button onClick={() => setSelectedThreadId(null)} className="text-gray-400 hover:text-gray-600 md:hidden">
                <ChevronLeft size={20} />
              </button>
              <div>
                <p className="font-semibold text-gray-900">{selected?.subject}</p>
                <p className="text-xs text-gray-500">{selected?.userEmail} · {selected?.messages?.length} message{selected?.messages?.length !== 1 ? "s" : ""}</p>
                {selected?.recipient && <p className="text-xs text-blue-500">to {selected.recipient}</p>}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {selected?.messages?.map((msg: any, i: number) => <MessageBubble key={i} msg={msg} />)}
              <div ref={bottomRef} />
            </div>

            <div className="p-4 border-t">
              <textarea value={replyBody} onChange={e => setReplyBody(e.target.value)}
                placeholder="Write a reply..." rows={3}
                className="w-full border rounded-lg px-3 py-2 text-sm resize-none focus:outline-hidden focus:ring-2 focus:ring-ring" />
              <div className="flex justify-end mt-2">
                <Button onClick={sendReply} disabled={isSending || !replyBody.trim()} className="flex items-center gap-2">
                  <Send size={14} />
                  {isSending ? "Sending..." : "Send Reply"}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
